import { Injectable } from '@angular/core';
import { AlertService } from '../../../core/services/alert.service';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  Observable,
  tap,
  throwError,
} from 'rxjs';
import { TVDModel } from '../types/tvd-model';
import {
  custTile,
  tvdData,
  availablePrograms,
  TVDProductDetails,
  TVDLocationDetails,
  TVDMetricGroupResponse,
  TvdMetricData,
  eroiIds,
  CustomAddedMetric,
  tvdGroupIds,
  ModifiedFormData,
  TVDRateDetailsResponse,
  TvdLocationServicesDetails,
  MetricConfig,
  availableBrandGroups,
  TvdValuePillar,
  TvdImpactCategory,
  TvdValuePillarToTileIds,
  TvdLocationAdvancedFilter,
  TvdProductAdvancedFilter,
  TvdAdvancedFilterOption,
  TvdDrillInMetricTiles,
  TVDMetricDrilldownDetails,
  TvdByProgramData,
  TvdByProgramDetailsData,
  TvdProgramToGroupIdMapping,
  TvdValuePillarToEroiIdMapping,
} from 'src/app/features/total-value-delivery/types/tvd-types';
import { Guid } from 'guid-typescript';
import { CustomTileItemComponent } from 'src/app/features/total-value-delivery/components/custom-tile-item/custom-tile-item.component';
import { TvdRequests } from 'src/app/core/services/tvd-requests.service';
import { EroiMetricComponent } from '../components/eroi-metric/eroi-metric.component';
import { AxiosResponse } from 'axios';
import { TvdNavbarMetricsConfig } from '../types/tvd-navbar-metrics-config';
import { PestTvdNavbarMetricsConfig } from '../types/pest-tvd-navbar-metrics-config';
import { QsrTvdNavbarMetricsConfig, FrsTvdNavbarMetricsConfig } from '../types/generic-metric-configs.builder';
import { CapitalizePipe } from 'src/app/core/pipes/capitalize.pipe';
import { Division, MetricCostAssumptions } from 'src/app/core/services/ebr-api-client/ebr-api-client.g';
import { CurrencyUomService } from 'src/app/core/services/currency-uom.service';

/**
 * TVD Data Ownership
 * ------------------
 *
 * TVDService is responsible for retrieving, storing, and emitting
 * TVD models.
 *
 * Business calculations for standard TVD metrics are owned by
 * the backend API and should not be reimplemented in the client.
 *
 * Frontend calculations should be limited to client-generated
 * features such as Custom Tiles and presentation-layer
 * aggregations that cannot be produced by the API.
 *
 * Metric Calculation Ownership
 * ----------------------------
 *
 * Standard TVD calculations are owned by the backend API.
 *
 * The API is responsible for producing:
 *  - monetaryContribution
 *  - modifiedMonetaryContribution
 *
 * for both individual metrics and summary metrics.
 *
 * Frontend code should treat API metric values as immutable and
 * should not recalculate or overwrite standard TVD metrics.
 *
 * Client-side calculation is limited to features that exist only
 * in the UI (e.g. Custom Tiles and their aggregations).
 *
 * This establishes the API as the single source of truth for TVD
 * calculations and prevents calculation drift between the backend
 * and frontend.
 */

/**
 * TODO (Architecture Evolution):
 * TVDService currently contains a mixture of:
 *
 * - API operations
 * - State management
 * - Model construction
 * - Workflow coordination
 * - UI-related state
 *
 * This structure evolved organically and is being incrementally
 * refactored toward the following responsibilities:
 *
 * Types         → define
 * Resolvers     → decide
 * Builders      → construct
 * Stores        → hold state
 * Services      → perform
 * Orchestrators → coordinate
 *
 * Future refactoring should favor moving workflow coordination
 * into orchestrators while keeping TVDService focused on data
 * retrieval, state updates, and TVD-specific capabilities.
 */
@Injectable({
  providedIn: 'root'
})
export class TVDService {
  tvdResponse: TVDMetricGroupResponse[] = [];

  // holds metrics selected by the user, default to all metrics. for ppt generation
  overallTVDModelSelections : TVDMetricGroupResponse = {id: '', brandGroup: '', indvMetrics: [], summMetrics: []};

  // holds metric selections used by FE components and services
  selectedItemsDict: Map<string, [data:tvdData, isSelected:boolean, isAddedToPres:boolean]> | undefined = new Map();

  //key is the tile's title
  custTileDict : Map<string, custTile> | undefined= new Map();
  //holds custom metrics formatted for ppt download
  addedMetrics : CustomAddedMetric[] | undefined = [];
  // True once the user has explicitly committed selections via "Add to Presentation".
  // Only reset when TVD data reloads (setToDefaultTVDModel / setTVDModelToUndefined).
  // Not reset by updateCustomTileDict — adding a tile re-uses the current selection.
  hasExplicitSelections: boolean = false;

  // TVD PPT model Observable
  private tvdSource = new BehaviorSubject<TVDModel | undefined>(undefined);
  tvdObservable = this.tvdSource.asObservable();

  // Custom Tiles Observable
  private customTilesSource = new BehaviorSubject<Map<string, custTile> | undefined>(this.custTileDict);
  customTilesObservable = this.customTilesSource.asObservable();

  tvdCustomCostTotal: number = 0;
  countMetric:number =0;
  // Current values in the edit panel
  currCustCost: string | undefined;
  currCustValue: string | undefined;
  currCustUnit: string | undefined;
  currCustTitle: string | undefined;
  currCustValuePillar : string | undefined;
  currCustImpactCategory : string | undefined;

  // Keeps track of all the Custom Tile objects, not just the data
  custTileObjectList : CustomTileItemComponent[] | undefined;

  // Observable for TVD product
  private tvdProductDetailsDataSubject: BehaviorSubject<any> = new BehaviorSubject<TVDProductDetails | undefined>(undefined);
  tvdProductDetailsDataObservable: Observable<TVDProductDetails | undefined> = this.tvdProductDetailsDataSubject.asObservable();

  // Observable for TVD Program selected
  private tvdProgramSelectedDataSubject: BehaviorSubject<any> = new BehaviorSubject<string | undefined>(availablePrograms.ALL);
  currentProgramSelectedObservable: Observable<string | undefined> = this.tvdProgramSelectedDataSubject.asObservable();
  private getCurrentProgram(): string {
    return this.tvdProgramSelectedDataSubject.getValue() ?? availablePrograms.ALL;
  }

  // Map to hold pest component data.
  private updateComponentDataForCostAssumptions: {[key: string]: BehaviorSubject<EroiMetricComponent | undefined>;} =
  {
    [eroiIds.NOCHARGEADDITIONALSERVICES]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.SETUPSERVICES]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.PESTPREVENTIONFINDINGS]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.ROOMNIGHTSSAVED]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.FURNITUREPRESERVATIONASSETS]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.FURNITUREPRESERVATIONENERGY]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.WATER]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.ENERGY]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
    [eroiIds.PRODUCTIVITY]: new BehaviorSubject<EroiMetricComponent | undefined>(undefined),
  };

  private originalMonetaryContributions: Map<string, string> = new Map([
    ['tvd_overall', ''],
    ['water', ''],
    ['energy', ''],
    ['productivity', ''],
    [eroiIds.NOCHARGEADDITIONALSERVICES, ''],
    [eroiIds.GENERALCONTRACTORCOSTAVOIDANCE, ''],
    [eroiIds.SETUPSERVICES, ''],
    [eroiIds.PESTPREVENTIONFINDINGS, ''],
    [eroiIds.ROOMNIGHTSSAVED, ''],
    [eroiIds.FURNITUREPRESERVATIONASSETS, ''],
    [eroiIds.FURNITUREPRESERVATIONENERGY, ''],

  ]);

  private monetaryContributions$ : BehaviorSubject<Map<string, string>> = new BehaviorSubject<Map<string, string>>(this.originalMonetaryContributions);
  monetaryContributionObservable: Observable<Map<string, string>> = this.monetaryContributions$.asObservable();

  private totalTotalValueDelivered$: BehaviorSubject<tvdData | undefined> = new BehaviorSubject<tvdData | undefined>(undefined);
  totalTotalValueDeliveredObservable: Observable<tvdData | undefined> = this.totalTotalValueDelivered$.asObservable();

  private modifiedMonetaryContributions$: BehaviorSubject<Map<string, string>> = new BehaviorSubject<Map<string, string>>(new Map());
  modifiedMonetaryContributionObservable: Observable<Map<string, string>> = this.modifiedMonetaryContributions$.asObservable();

  // Tracks the currency code that is the basis for the current cost assumptions values.
  // This is used to avoid multiple unnecessary currency conversions in tvd-sidenavbar component.
  private showLoadingOverlay$ : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  showLoadingOverlayObservable: Observable<boolean> = this.showLoadingOverlay$.asObservable();

  // TODO: The method name suggests that this is a UI concern, but the service is a data service. Consider moving this to a UI component or a UI service.
  // TODO: Consider renaming to represent a loading status for the TVD data such as setIsDataLoading instead of showLoadingOverlay, rather than a UI concern.
  // TODO: Consider splitting this into two methods, one for starting the data load and the other for completing the data load, to make it more idiomatic in TypeScript.
  public setShowLoadingOverlay(value: boolean): void {
    this.showLoadingOverlay$.next(value);
  }

  // TODO: The method name suggests that this is a UI concern, but the service is a data service. Consider moving this to a UI component or a UI service.
  // TODO: Consider renaming to represent a loading status for the TVD data such as getIsDataLoading instead of showLoadingOverlay, rather than a UI concern.
  // TODO: Consider converting this into a getter property instead of a method and rename it to isDataLoading, to make it more idiomatic in TypeScript.
  public getShowLoadingOverlay(): boolean {
    return this.showLoadingOverlay$.getValue();
  }

  // Setter for TVD Program selected
  setTvdProgramSelected(value: string | undefined) {
    this.tvdProgramSelectedDataSubject.next(value);
  }

  //setter and getter for TVD product
  setTvdProductDetailsData(value: TVDProductDetails | undefined) {
    this.tvdProductDetailsDataSubject.next(value);
  }

  getTvdProductDetailsData(): TVDProductDetails | undefined {
    return this.tvdProductDetailsDataSubject.getValue();
  }

  // Observable for TVD location
  private tvdLocationDetailsDataSubject: BehaviorSubject<any> = new BehaviorSubject<TVDLocationDetails | undefined>(undefined);
  tvdLocationDetailsDataObservable: Observable<TVDLocationDetails | undefined > = this.tvdLocationDetailsDataSubject.asObservable();

  //setter and getter for TVD location
  setTvdLocationDetailsData(value: TVDLocationDetails | undefined) {
    this.tvdLocationDetailsDataSubject.next(value);
  }

  // Observables, setter nad getters for Pest TVD locations data
  private tvdPestLocationDetailsDataSubject: BehaviorSubject<any> = new BehaviorSubject<TvdLocationServicesDetails | undefined>(undefined);
  tvdPestLocationDetailsDataObservable: Observable<TvdLocationServicesDetails | undefined > = this.tvdPestLocationDetailsDataSubject.asObservable();

  setPestTvdLocationDetailsData(value: TvdLocationServicesDetails | undefined) {
    this.tvdPestLocationDetailsDataSubject.next(value);
  }

  getPestTvdLocationDetailsData(): TvdLocationServicesDetails | undefined {
    return this.tvdPestLocationDetailsDataSubject.getValue();
  }

  getTvdLocationDetailsData(): TVDLocationDetails | undefined {
    return this.tvdLocationDetailsDataSubject.getValue();
  }

  private tvdModalButtonActionSubject = new BehaviorSubject<string | undefined>('');
  tvdModalButtonAction$ = this.tvdModalButtonActionSubject.asObservable();


  setTvdModalButtonAction(action: string | undefined) {
    this.tvdModalButtonActionSubject.next(action);
  }

  public updateModifiedMonetaryContributions(metricName: string, value: string) {
    const currentMap = this.modifiedMonetaryContributions$.getValue();
    currentMap.set(metricName, value);
    this.modifiedMonetaryContributions$.next(currentMap);
  }

  public getModifiedMonetaryContributionValue(key: string): string {
    return this.modifiedMonetaryContributions$.getValue().get(key) || '0';
  }

  // Observable and getter setters to determine if initial load is complete for TVD
  private tvdInitialLoadComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  tvdInitialLoadCompleteObservable: Observable<boolean> = this.tvdInitialLoadComplete$.asObservable();

  public setTvdInitialLoadComplete(value: boolean) {
    this.tvdInitialLoadComplete$.next(value);
  }

  public getTvdInitialLoadComplete(): boolean {
    return this.tvdInitialLoadComplete$.getValue();
  }

  // Observable and getter setters to show TVD Titles in enterprise terms
  private showInEnterpriseTerms$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  showInEnterpriseTermsObservable: Observable<boolean> = this.showInEnterpriseTerms$.asObservable();

  public setShowInEnterpriseTerms(value: boolean) {
    this.showInEnterpriseTerms$.next(value);
  }

  public getShowInEnterpriseTerms(): boolean {
    return this.showInEnterpriseTerms$.getValue();
  }

  // This is to limit the number of times the user is shown the warning about the tile limit,
  // to prevent excessive alerts when the user is trying to select metrics for their presentation.
  // Also prevent additional metrics being selected when the limit is reached.
  private selectedMetricsLimit$: BehaviorSubject<Record<TvdValuePillar, boolean>> = new BehaviorSubject<Record<TvdValuePillar, boolean>>({
    [TvdValuePillar.BusinessOutcomes]: false,
    [TvdValuePillar.OperationalPerformance]: false,
    [TvdValuePillar.EnvironmentalImpact]: false
  });
  selectedMetricsLimitObservable: Observable<Record<TvdValuePillar, boolean>> = this.selectedMetricsLimit$.asObservable();

  public setSelectedMetricsLimit(valuePillar: TvdValuePillar, value: boolean) {
    const currentLimits = this.selectedMetricsLimit$.getValue();
    currentLimits[valuePillar] = value;
    this.selectedMetricsLimit$.next(currentLimits);
  }

  public getSelectedMetricsLimit(valuePillar: TvdValuePillar): boolean {
    return this.selectedMetricsLimit$.getValue()[valuePillar];
  }

  private formData: ModifiedFormData = {};

  /**
   * Sets the form data.
   *
   * @param data - The form data to be set.
   */
  setFormData(data: ModifiedFormData) {
    this.formData = structuredClone(data);
  }

  /**
   * Retrieves the form data.
   *
   * @returns {FormData} The current form data.
   */
  getFormData(): ModifiedFormData {
    return this.formData;
  }

  updateTvdData(data:TVDModel){
    this.tvdSource.next(data);
  }

  /**
   * @deprecated
   */
  public updateMonetaryContributions(metricName: string,value:string) {
    // No Charge Additional Services metric can be set to zero or greater,
    // but other metrics should only be set to values greater than zero.
    if(metricName === eroiIds.NOCHARGEADDITIONALSERVICES)
    {
      if (this.originalMonetaryContributions.has(metricName)
          && Number(value.replace(/,/g, '')) >= 0
          && this.originalMonetaryContributions.get(metricName) === ''
      ) {
        this.originalMonetaryContributions.set(metricName, value);
        this.monetaryContributions$.next(this.originalMonetaryContributions);
      }
    }
    else
    {
      if (this.originalMonetaryContributions.has(metricName)
          && Number(value.replace(/,/g, '')) > 0
          && Number(this.getMonetaryContributionValue(metricName)) === 0
      ) {
        this.originalMonetaryContributions.set(metricName, value);
        this.monetaryContributions$.next(this.originalMonetaryContributions);
      }
    }
  }

  /**
   * @deprecated
   */
  public getMonetaryContributionValue(key: string): string {
    return this.originalMonetaryContributions.get(key) || '0';
  }

  public updateTotalValueDelivered(data: tvdData) {
    // Update the total value delivered subject
    this.totalTotalValueDelivered$.next(data);
    // Retrieve the current TVD data
    const currentData = this.tvdSource.getValue();
    if (!currentData)
        return;

    // Safely navigate to the default TVD object
    const metricId = this.getGroupId();
    const defaultTvd = currentData.tvdResponse.find(item => item.id === metricId);
    if (!defaultTvd)
        return;

    // Safely locate the overall TVD summary metric
    const summMetric = defaultTvd.summMetrics.find(metric => metric.id === "tvd_overall");
    if (!summMetric)
      return;

    // Update the summary metric with new monetary contribution data
    summMetric.modifiedMonetaryContribution =
      data.modifiedMonetaryContribution != null
        ? data.modifiedMonetaryContribution
        : data.monetaryContribution;

    // Push the updated data back to the source to trigger UI updates
    this.tvdSource.next(currentData);
  }
  /**
   * Resets the form data by creating a new instance of FormData.
   * This method clears any existing data in the form.
   */
  resetFormData() {
    this.formData = {};
  }

  resetCostAssumptionsMetrics(){
    this.resetFormData();
    this.setTvdModalButtonAction('');
    this.monetaryContributions$.next(this.originalMonetaryContributions);
    this.totalTotalValueDelivered$.next(undefined);
    this._tvdRateDetailsData = null;
    this.resetPestComponentsData();
    this.resetOriginalMonetaryContributions();
  }

  resetPestComponentsData() {
    Object.keys(this.updateComponentDataForCostAssumptions).forEach(key => {
      // Reset all components to undefined
      this.updateComponentDataForCostAssumptions[key].next(undefined);
    });
  }

  /**
   * @deprecated
   */
  resetOriginalMonetaryContributions(){
    this.originalMonetaryContributions.forEach((value, key) => {
      // Reset all components to original values
      this.originalMonetaryContributions.set(key, '');
    });
  }

  updateComponentData(key: string, data: EroiMetricComponent) {
    if (data !== undefined && this.updateComponentDataForCostAssumptions[key]) {
      this.updateComponentDataForCostAssumptions[key].next(data);
    }
  }

  getCostAssumptionsComponentData(key: string): EroiMetricComponent | undefined {
    return this.updateComponentDataForCostAssumptions[key]?.getValue();
  }

  constructor(
    private _tvdRequests : TvdRequests,
    private _alertService : AlertService,
    private _currencyUomService: CurrencyUomService,
    private _capitalizePipe: CapitalizePipe
    ) {
      this.currCustCost = "";
      this.custTileObjectList = [];
  }

  private programData = new BehaviorSubject<any>(availablePrograms.ALL);
  private tvdMetricAdded = new BehaviorSubject<any>([]);
  private division : string = '';

  setProgramData(data: any) {
    this.programData.next(data);
  }

  setTvdMetricData(data:any){
    this.tvdMetricAdded.next(data);
  }

  getProgramData():Observable<any>{
    return this.programData.asObservable();
  }

  getTvdMetricData(): Observable<any>{
    return this.tvdMetricAdded.asObservable();
  }

  setDivision(division: string) {
    this.division = division;
  }

  getDivision(): string {
    return this.division;
  }

  async getTVDData(costAssumptionsMetrics?:MetricCostAssumptions) {
    return await this._tvdRequests.getTotalValueDelivered(costAssumptionsMetrics).then(response => {
      const { data } = response;
      if (data != undefined) {
        return data;
      }
      }).catch(error => {
        this._handleError(`Retrieving TVD data failed.`, error);
      });
  }

  isDataZeroOrEmpty(tvdData: tvdData): boolean {
    return tvdData.monetaryContribution == '0' && (tvdData.valueContribution == '0' || tvdData.valueContribution === '');
  }

  /**
   * Returns the total number of metrics (standard + custom tiles) for a given value pillar.
   * Used by the download button to validate the per-pillar limit before generating the PPT.
   *
   * Standard metrics are sourced from the raw TVD API response.
   * Custom tiles are sourced from custTileDict.
   */
  countTotalMetricsPerValuePillar(valuePillar: TvdValuePillar): number {
    const groupID = this.getGroupId();
    const apiMetrics = this.tvdResponse.find(g => g.id === groupID)?.indvMetrics ?? [];
    const ids = new Set(TvdValuePillarToTileIds[valuePillar]);
    const standardCount = this.normalizeApiMetricIds(apiMetrics).filter(id => ids.has(id)).length;

    let customCount = 0;
    if (this.custTileDict) {
      for (const [, tile] of this.custTileDict) {
        if (tile.valuePillar === valuePillar) customCount++;
      }
    }

    return standardCount + customCount;
  }

  /**
   * Collapses the API's split 'repair' + 'replacement' entries into a single 'assets'
   * ID, matching the single Assets tile displayed in the UI.
   */
  private normalizeApiMetricIds(metrics: TvdMetricData[]): string[] {
    const seen = new Set<string>();
    const ids: string[] = [];
    for (const m of metrics) {
      const id = (m.id === 'repair' || m.id === 'replacement') ? eroiIds.ASSETS : m.id;
      if (!seen.has(id)) { seen.add(id); ids.push(id); }
    }
    return ids;
  }

  countSelectedItems(selectedItemsDictTvd:  Map<string, [data:tvdData, isSelected:boolean, isAddedToPres:boolean]>): number {
    let itemCount: number = 0;
    for (let [key, value] of selectedItemsDictTvd) {
      if (value[1] && !this.isDataZeroOrEmpty(value[0])) {
        itemCount++;
      }
    }
    this.countMetric = itemCount;
    return itemCount;
  }

  getCountMetric():number{
    return this.countMetric;
  }

  private _handleError(errorMessage: string, error: any) {
    console.log(decodeURI(encodeURI(errorMessage + ':' + error)));
    this._alertService.addAlert({
      message: `${errorMessage} If problem persists, please report a bug.`,
      id: Guid.create()
    });
  }

  updateCustomTileDict(model : Map<string, custTile>){
    this.custTileDict = model;
    this.customTilesSource.next(this.custTileDict);
    this.addCustomTotal();
  }

  /**
   * Updates the overall TVD metric in the selections model to include custom tile contributions
   */
  private addCustomTotal() {
    //Get total cost of custom tiles
    this.tvdCustomCostTotal = Array.from(this.custTileDict!.values()).reduce((sum, current) => sum += parseFloat(current.cost.replace(/,/g, '')), 0);

    // Get Overall TVD metric in Selections model (which is the one used in the ppt)
    const tvdOverallMetric = this.overallTVDModelSelections.summMetrics.find(metric => metric.id === eroiIds.TOTALVALUEDELIVERED);

    // update total value delivered by adding custom tiles total and total from response
    if(tvdOverallMetric) {
      const tvdOverallCostTotal = parseFloat(
        this.findMetricInOverallResponse(eroiIds.TOTALVALUEDELIVERED, true)?.monetaryContribution?.replace(/,/g, '') || '0'
      );
      tvdOverallMetric.monetaryContribution = (this.tvdCustomCostTotal + tvdOverallCostTotal).toLocaleString();
    }
    //make sure custom tiles that are already added to the presentation reflect the new total in the ppt properly
    this.confirmDataUpdate();
  }

  updateAddedMetrics(addedMetrics : CustomAddedMetric[]){
    this.addedMetrics = addedMetrics;
  }

  getGroupId() {
    let groupID = tvdGroupIds.DEFAULT;
    if(this.division.toLowerCase() === "pest") {
      groupID = tvdGroupIds.PEST;
    }
    return groupID;
  }

  /**
   * @deprecated Use TvdKpiCardBuilder.build() instead.
   * 
   * Builds the standard non-pest KPI card array used by both the TVD
   * details page and the TVD dialog drilldown. Single source of truth.
   */
  public buildNonPestKpiCards(params: {
    metricId: string;
    costAssumptionValue: number;
    costAssumptionValue2: number;
    isNoDollar: boolean;
    tvdDollars: number;
    modifiedTVDDollars: number;
    metricUnits: string;
    modifiedTvdMetricQuantity: number;
    totalLocations: string;
    totalContributingProducts: string;
    metricName: string;
    currencySymbol: string;
    avgTVDMetricDollars: number;
    modifiedAvgTVDMetricDollars: number;
    avgTVDMetricQuantity: number;
  }, isModifiedValueHighlighted: boolean=false): TvdDrillInMetricTiles[] {
    const hasCostAssumption = params.costAssumptionValue > 0 || params.costAssumptionValue2 > 0;
    const metricNameCapitalized = this._capitalizePipe.transform(params.metricName);
    const metricUnitsCapitalized = this._capitalizePipe.transform(params.metricUnits);

    return [
      {
        id: 'estimated-tvd-metric',
        title: 'Estimated Total Value',
        originalValue: hasCostAssumption ? (params.isNoDollar ? 'N/A' : params.tvdDollars) : '',
        isCurrency: !params.isNoDollar,
        modifiedValue: params.isNoDollar ? 'N/A' : params.modifiedTVDDollars,
        previewCssClass: isModifiedValueHighlighted ? 'text-neptune-default' : '',
        isModifiedValueHighlighted: isModifiedValueHighlighted
      },
      {
        id: 'tvd-metric-units',
        title: metricUnitsCapitalized ?? params.metricUnits,
        modifiedValue: params.metricName === 'Cost Avoidance' ? 'N/A' : params.modifiedTvdMetricQuantity,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-total-locations',
        title: 'Locations',
        modifiedValue: params.totalLocations,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-total-contributing-products',
        title: '# Contributing Products',
        modifiedValue: params.totalContributingProducts,
        isCurrency: false,
        originalValue: '',
        previewCssClass:'',
        isModifiedValueHighlighted: false
      },
      {
        id: 'tvd-avg-metric-per-location',
        title: `Average ${metricNameCapitalized} ${params.currencySymbol}/Location`,
        originalValue: hasCostAssumption ? (params.isNoDollar ? 'N/A' : params.avgTVDMetricDollars) : '',
        isCurrency: !params.isNoDollar,
        modifiedValue: params.isNoDollar ? 'N/A' : params.modifiedAvgTVDMetricDollars,
        previewCssClass: isModifiedValueHighlighted ? 'text-neptune-default' : '',
        isModifiedValueHighlighted: isModifiedValueHighlighted
      },
      {
        id: 'tvd-avg-metric-units-per-location',
        title: `Average ${metricUnitsCapitalized}/Locations`,
        modifiedValue: params.metricName === 'Cost Avoidance' ? 'N/A' : params.avgTVDMetricQuantity,
        isCurrency: false,
        originalValue: '',
        previewCssClass: '',
        isModifiedValueHighlighted: false
      }
    ];
  }


  /**
   * TODO (Currency Refresh Orchestrator):
   * This method coordinates a multi-step workflow:
   * - Reloading TVD data
   * - Rebuilding presentation selections
   * - Updating metric visibility
   * - Emitting updated UI models
   *
   * If currency refresh logic continues to grow, consider moving this
   * workflow into a dedicated orchestrator responsible for handling
   * currency-driven TVD refresh operations.
   */
  async refreshTVDDataAfterCurrencyChange(){
    let groupID = this.getGroupId();
    this.tvdResponse = await this.getTVDData();
    if(this.tvdResponse!=undefined){
      //default presentation metrics to all, creating a deep clone as we do this
      this.overallTVDModelSelections = {
        id: groupID,
        brandGroup: this.tvdResponse.find(group => group.id === groupID)?.brandGroup || '',
        indvMetrics: JSON.parse(JSON.stringify(this.tvdResponse.find(group => group.id === groupID)?.indvMetrics || [])),
        summMetrics: JSON.parse(JSON.stringify(this.tvdResponse.find(group => group.id === groupID)?.summMetrics || []))
      };
      // this.addedMetrics = [];
      // this.custTileDict = new Map();

      // Set the TVd metrics based on their presence in the response
      // Update the config based on the division with metric presence
      const metricResponse = this.checkMetricPresence(this.tvdResponse, this.division);
      this.updateConfigBasedOnPresence(metricResponse, this.division);
    }

    this.confirmDataUpdate();
  }

  /**
   * TODO (Orchestration Refactor):
   * This method coordinates multiple workflow steps:
   * - API retrieval
   * - Selection initialization
   * - Configuration updates
   * - UI model updates
   *
   * Consider introducing a dedicated TVD initialization orchestrator
   * if additional initialization workflows are introduced.
   */
  async setToDefaultTVDModel() {
    let groupID = this.getGroupId();
    this.tvdResponse = await this.getTVDData();
    if(this.tvdResponse!=undefined){
      //default presentation metrics to all, creating a deep clone as we do this
      this.overallTVDModelSelections = {
        id: groupID,
        brandGroup: this.tvdResponse.find(group => group.id === groupID)?.brandGroup || '',
        indvMetrics: JSON.parse(JSON.stringify(this.tvdResponse.find(group => group.id === groupID)?.indvMetrics || [])),
        summMetrics: JSON.parse(JSON.stringify(this.tvdResponse.find(group => group.id === groupID)?.summMetrics || []))
      };
      this.addedMetrics = [];
      this.custTileDict = new Map();
      this.hasExplicitSelections = false;
      // Set the TVd metrics based on their presence in the response
      // Update the config based on the division with metric presence
      const metricResponse = this.checkMetricPresence(this.tvdResponse, this.division);
      this.updateConfigBasedOnPresence(metricResponse, this.division);
    }

    this.confirmDataUpdate();
  }

  //Checks if the TVD response contains the expected metrics based on the division
  //Returns an object with boolean values indicating the presence of each metric
  checkMetricPresence(tvdResponse: any[], division: string) {
    const metricIds = tvdResponse?.[0]?.indvMetrics?.map((m: any) => m.id) || [];

    if (['Institutional', 'QSR', 'FRS'].includes(division)) {
      return {
        productivity: metricIds.includes(eroiIds.PRODUCTIVITY),
        water: metricIds.includes(eroiIds.WATER),
        energy: metricIds.includes(eroiIds.ENERGY),
      };
    } else if (division === 'Pest') {
      return {
        nochargeadditionalcomultiplier: metricIds.includes(eroiIds.NOCHARGEADDITIONALSERVICES),
        generalcontractorcostavoidance: metricIds.includes(eroiIds.GENERALCONTRACTORCOSTAVOIDANCE),
        setupservicescomultiplier: metricIds.includes(eroiIds.SETUPSERVICES),
        preventionfindings: metricIds.includes(eroiIds.PESTPREVENTIONFINDINGS),
        roomprice: metricIds.includes(eroiIds.ROOMNIGHTSSAVED),
        furniturepreservationassets: metricIds.includes(eroiIds.FURNITUREPRESERVATIONASSETS),
        furniturepreservationenergy: metricIds.includes(eroiIds.FURNITUREPRESERVATIONENERGY)
      };
    }

    return {};
  }

  //Updates the TVD config based on the presence of metrics in the response
  updateConfigBasedOnPresence(metricPresence: any, division: string) {
    const configMap: { [key: string]: MetricConfig[] } = {
      'Institutional': TvdNavbarMetricsConfig,
      'QSR': QsrTvdNavbarMetricsConfig,
      'FRS': FrsTvdNavbarMetricsConfig,
      'Pest': PestTvdNavbarMetricsConfig
    };

    const config = configMap[division];
    if (!config) return;

    config.forEach((group: MetricConfig) => {
      group.metrics.forEach((metric) => {
        if (division === 'Institutional') {
          if (['hospitality', 'foodservice'].includes(metric.id)) {
            metric.isHidden = !metricPresence.productivity;
          } else {
            metric.isHidden = !metricPresence[metric.id];
          }
        } else {
          // For QSR, FRS, and Pest divisions
          metric.isHidden = !metricPresence[metric.id];
        }
      });
    });
  }

  setTVDModelToUndefined(){
    this.tvdResponse = [];
    this.overallTVDModelSelections = {id: '', brandGroup: '', indvMetrics: [], summMetrics: []};
    this.custTileDict?.clear();
    this.custTileDict = undefined;
    this.addedMetrics = undefined;
    this.hasExplicitSelections = false;

    // Reset local storage for TVD
    sessionStorage.removeItem('selectedItemsDictSession');
    this.tvdSource.next(undefined);
  }

  /**
   * TODO (Store Separation):
   * This method is responsible for constructing and emitting the current
   * TVD model to subscribers.
   *
   * If a dedicated TVD store is introduced in the future, consider moving
   * model emission and observable state management into that store,
   * leaving TVDService responsible for API operations only.
   */
  confirmDataUpdate(){
    let groupID = this.getGroupId();
    let newModel = new TVDModel(
      this.tvdResponse,
      // use selections model for "All" tab, and response models for programs
      [this.overallTVDModelSelections].concat(this.tvdResponse.filter(group => group.id !== groupID)),
      [], //default TVD page doesn't use TvdMetricGroups for its UI at this time
      this.custTileDict,
      this.addedMetrics
      );

    this.tvdSource.next(newModel);
  }

  /**
   * Updates selections model with user selections
   * @param selectedItemsDict Map representing selected items (including custom tiles)
   */
  updateSelectionsPayload(selectedItemsDict: Map<string, [data: tvdData, isSelected: boolean, isAddedToPres: boolean]>) {
    // reset all individual metrics in selections model to make updating easier
    this.overallTVDModelSelections.indvMetrics = [];
    this.addedMetrics = [];
    this.hasExplicitSelections = true;

    selectedItemsDict.forEach((value, id) => {
      const isAddedToPres = value[2];
      if (isAddedToPres) {
          this.addMetricToPresentation(id);
      }
    });

    this.confirmDataUpdate();
  }

  private tvdRateDetailsSubject = new BehaviorSubject<TVDRateDetailsResponse | null>(null);
  private _tvdRateDetailsData: Observable<TVDRateDetailsResponse> | null = null;
  // TODO: Make the trigger fetching the TVD rates idempotent and safe to call from anywhere
  public tvdRateDetailsData$(division: string, regions: string[]): Observable<TVDRateDetailsResponse> {
    const currencyCode = this._currencyUomService.getCurrency();
    const uom = this._currencyUomService.getUom();

    this._tvdRateDetailsData = from(this._tvdRequests.getDefaultTVDRates(division, regions, currencyCode, uom)).pipe(
      map((response: AxiosResponse) => {
        const data = response.data;
        if (data.statusCode === 'OK') {
          return {
            data: data.data,
            tvdRateDetails:data.data.tvdRateDetails,
            regions:data.data.regions,
            division:data.data.division
          };
        } else {
          return {
            data: [],
            statusCode: data.statusCode,
            regions: [],
            tvdRateDetails: [],
            division:''
          };
        }
      }),
      tap(response => this.tvdRateDetailsSubject.next(response)),
      catchError((error) => {
        this._handleError('Retrieving Default TVD Rates Data failed.', error);
        return throwError(() => new Error('Failed to retrieve TVD Rates Data')); // Returning an error observable
      })
    );
    return this._tvdRateDetailsData;
  }

  public getDefaultRate(metricId: string, country: string | null, isDecimal: boolean = false): number | string {
    const details = this.tvdRateDetailsSubject.getValue();
    if (!details || !details.tvdRateDetails) {
      return 1;
    }

    const rate = (details.tvdRateDetails.find(x => x.id === metricId && (country === null || x.country === country))?.rate ?? 0).toFixed(2);
    return isDecimal ? rate : parseFloat(rate);
  }

  /**
   * Adds a single metric in selections to ppt selections model.
   * Directs execution to correct logic based on metric ID
   * @param id id of the metric to add to the presentation
   */
  private addMetricToPresentation(id: string) {
    if (id === eroiIds.ASSETS) { // Assets metric is split into two data points so we need to add both
      this.addTvdMetricById("repair");
      this.addTvdMetricById("replacement");
    } else if (Object.values(eroiIds).includes(id as any)) { // Add TVD Metric
        this.addTvdMetricById(id);
    } else { // Add Custom Metric
      this.addCustomMetricToPresentation(id);
    }
  }

  /**
   * Adds a single TVD metric to ppt selections model
   * @param id : id of the metric to add
   */
  private addTvdMetricById(id: string) {
    const originalMetric = this.findMetricInOverallResponse(id);
    if (originalMetric) {
      const newMetric: TvdMetricData = {
        '$type': originalMetric['$type'],
        id: id,
        brand: originalMetric.brand,
        sortSequenceNumber: originalMetric.sortSequenceNumber,
        valueContribution: originalMetric.valueContribution ?? '0',
        monetaryContribution: originalMetric.monetaryContribution,
        modifiedMonetaryContribution: originalMetric.modifiedMonetaryContribution,
        modifiedValueContribution: originalMetric.modifiedValueContribution,
      };
      this.overallTVDModelSelections.indvMetrics.push(newMetric);

    }
  }

  /**
   * Adds a single custom metric to ppt selections model
   * @param id : Title of custom tile to add
   */
  public addCustomMetricToPresentation(id: string) {
    let customTile = this.custTileDict?.get(id);
    if(customTile && this.addedMetrics){
      // Map values to object to pass to ppt
      let addedMetric : CustomAddedMetric = {
        Title : customTile.title,
        Value : customTile.metricValue.toString(),
        ValueUnit : customTile.unit,
        Cost: customTile.cost,
        ValuePillar: customTile.valuePillar.replace(/\s+/g, ''), //remove spaces so enums can map to the enums in API
        ImpactCategory: customTile.impactCategory.replace(/\s+/g, '') //remove spaces so enums can map to the enums in API
      }

      this.addedMetrics.push(addedMetric);
    } else {
      this._handleError(`Error adding custom tile with title: ${id}`, { id: id });
    }
  }

  /**
   * Retrieves a metric from the Overall TVD set of metrics by ID.
   */
  private findMetricInOverallResponse(id: string, isSummary: boolean = false): TvdMetricData | undefined {
    let groupID = this.getGroupId();
    if(isSummary){
      return this.tvdResponse.find(group => group.id === groupID)?.summMetrics.find(metric => metric.id === id);
    }
    // Indivdiual metric
    return this.tvdResponse.find(group => group.id === groupID)?.indvMetrics.find(metric => metric.id === id);
  }

  // Institutional TVD Details Request
  async getTVDDetailsData(
    groupBy: string,
    metricName: string,
    programName: string,
    costAssumptionsMetrics?: MetricCostAssumptions,
    brandGroup?: availableBrandGroups,
    brands?: string[],
    locationAdvancedFilter?: TvdLocationAdvancedFilter,
    productAdvancedFilter?: TvdProductAdvancedFilter) {
    return await this._tvdRequests.getTVDDetailsMetrics(groupBy, metricName, programName, costAssumptionsMetrics, brandGroup, brands, locationAdvancedFilter, productAdvancedFilter).then(response => {
      const { data } = response;
      if (data != undefined) {
        return data;
      }
      }).catch(error => {
        this._handleError('Retrieving TVD Details Data failed.', error);
      });
  }

  async getTVDAdvancedFilterOptions(
    metricName: string,
    programName: string,
    brandGroup?: availableBrandGroups,
    brands?: string[]): Promise<TvdAdvancedFilterOption[] | undefined> {
    // TODO: Encapsulate the logic for converting the metricName into an eroid instead of having the caller do it and passing the eroid to the request function. This will make it easier to maintain and less error prone as we won't have to worry about callers passing in metric names that don't match the expected format.
    // The commented out line below is an example of how this could be done, but it would require changes to the request function and potentially other parts of the codebase so it may not be worth doing at this time.
    // const normalizedMetricName = this.normalizeMetricName(metricName);
    return await this._tvdRequests.getTVDAdvancedFilterOptions(metricName, programName, brandGroup, brands).then(response => {
      const { data } = response;
      if (data != undefined) {
        return data as TvdAdvancedFilterOption[];
      }
      return undefined;
    }).catch(error => {
      this._handleError('Retrieving TVD Filter Options failed.', error);
      return undefined;
    });
  }

  // Pest Services TVD Details Request
  async getTVDServicesDetailsData(groupBy: string, metricName: string, costAssumptionsMetrics?: MetricCostAssumptions) {
    return await this._tvdRequests.getTVDServicesDetailsMetrics(groupBy, metricName, costAssumptionsMetrics).then(response => {
      const { data } = response;
      if (data != undefined) {
        return data;
      }
      }).catch(error => {
        this._handleError('Retrieving TVD Details Data failed.', error);
      });
  }

  // Check if there are any active cost adjustments in the form data
  public hasActiveCostAdjustments(): boolean {
    return Object.keys(this.getFormData()).length > 0;
  }

  /**
   * Retrieves the TVD Program Table data.
   * This is used to populate the program level table on the TVD landing page.
   * @returns A promise that resolves to an array of TVD Program Table data.
   */
  async getTvdProgramTableData(): Promise<TvdByProgramData[]> {
    try {
      const response =
        await this._tvdRequests.getTotalValueDeliveredForPrograms();

      return response.data as TvdByProgramData[];
    } catch (error) {
      this._handleError('Retrieving TVD Program Table Data failed.', error);
      throw error;
    }
  }

  /**
   * Retrieves the TVD Program Details data.
   * This is used to populate the program level details when drilling into a program from the TVD landing page.
   * @param programName The name of the program for which to retrieve details.
   * @returns A promise that resolves to the TVD Program Details data.
   */
  async getTvdProgramDetailsData(
    programName: string,
  ): Promise<TvdByProgramDetailsData | undefined> {
    try {
      const response =
        await this._tvdRequests.getTotalValueDeliveredForProgramDetails(
          programName,
        );
      return response.data as TvdByProgramDetailsData;
    } catch (error) {
      this._handleError(
        `Retrieving TVD Details Data for program ${programName} failed.`,
        error,
      );
      throw error;
    }
  }

  /**
   * Retrieves the current TVD model.
   * @returns The current TVD model or undefined if not available.
   */
  // TODO: For consistency, name all methods that are wrappers to getValue() with "getCurrent" prefix, e.g., getCurrentFormData, getCurrentTvdModel, etc.
  public getCurrentTvdModel(): TVDModel | undefined {
    return this.tvdSource.getValue();
  }


  /**
   * Retrieves metrics for a given value pillar, based on division and selected scope.
   *
   * NOTE:
   * This method was moved from TvdValuePillarSummaryComponent to centralize
   * domain logic and avoid duplicating metric grouping behavior across components.
   */
  // TODO: This is doing TWO responsibilities: filtering metrics based on value pillar and division, and also retrieving the metrics from the TVD response. Consider splitting this into two methods for better separation of concerns.
  getMetricsForValuePillar(
    tvdResponseData: TVDMetricGroupResponse[],
    valuePillar: TvdValuePillar,
    division: string,
    selectedScope?: string
  ): TvdMetricData[] {

    if (!selectedScope) {
      console.warn('⚠︝ selectedScope is undefined');
      return [];
    }

    // Clone to prevent mutation of shared mapping
    const metricList = [...(TvdValuePillarToEroiIdMapping.get(valuePillar) ?? [])];

    // If metric list contains ASSETS, we need to add repair and replacement since Assets is calculated superficially in the UI
    // TODO: Need to understand the implications of adding REPAIR and REPLACEMENT enums to avoid magic strings
    // TODO (TECH DEBT):
    // 'replacement' and 'repair' are not part of InsitutionalMetricIds or any config,
    // but they are included in indvMetrics and summary totals.
    //
    // Because the current pipeline relies on component-backed metric data,
    // these model-only metrics must be hardcoded to ensure they are included in
    // sync + recalculation steps.
    //
    // This is a gap in the architecture: not all metrics are config-driven.
    //
    // FUTURE FIX:
    // Replace hardcoded metric lists with a dynamic solution that:
    // 1. Derives all metric IDs from tvdModel.indvMetrics
    // 2. Excludes composite metrics (via compositeTilesConfig)
    // 3. Treats remaining metrics as leaf metrics automatically
    if (metricList.includes(eroiIds.ASSETS)) {
      // TODO: Investigate why this is hardcoded here instead of being part of the mapping. It seems like a special case for the ASSETS metric, but it might be better to handle this in a more centralized way.
      metricList.push('repair', 'replacement');
    }

    let metricsForGroup: TvdMetricData[] = [];

    // TODO: The service knows about UI-level behavior (brand group vs program). Consider separating strategy per division or pass a resolver function to avoid hardcoding division logic here.
    if (division === Division.Distributor) {
      metricsForGroup =
        tvdResponseData.find(item =>
          item.id === tvdGroupIds.DEFAULT &&
          item.brandGroup === selectedScope
        )?.indvMetrics ?? [];
    } else if (division === Division.Pest) {
      // Pest logic
      metricsForGroup =
        tvdResponseData.find(item =>
          item.id === tvdGroupIds.PEST
        )?.indvMetrics ?? [];

    } else {
      // Institutional / default logic
      const programGroupId = selectedScope
        ? TvdProgramToGroupIdMapping.get(selectedScope)
        : undefined;

      metricsForGroup =
        tvdResponseData.find(item =>
          item.id === programGroupId
        )?.indvMetrics ?? [];
    }

    return metricsForGroup.filter(metric =>
      metricList.includes(metric.id)
    );
  }

  /**
   * <summary>
   * Calls the TVD API to recalculate all metrics using the provided cost assumptions.
   * This method delegates ALL calculation logic (cost adjustments, currency conversion,
   * and base metric aggregation) to the backend.
   * </summary>
   *
   * <param name="assumptions">
   * The in-memory cost assumptions provided by the user. These are not persisted
   * in the API and are only used for on-demand recalculation.
   * </param>
   *
   * <returns>
   * A Promise that resolves once the recalculated TVD data has been fetched
   * and emitted to subscribers.
   * </returns>
   *
   * <remarks>
   * - This method does NOT perform any calculations locally.
   * - The API response is treated as the single source of truth.
   * - Subscribers to tvdObservable will automatically receive updated data.
   * - Loading indicators are managed within this method.
   * </remarks>
   *
   * <exception>
   * Logs an error and displays an alert if the API call fails.
   * </exception>
   *
   * Calculation Ownership
   * ---------------------
   *
   * Standard TVD metric calculations are owned by the API.
   *
   * The API is responsible for returning both:
   *
   * - monetaryContribution
   * - modifiedMonetaryContribution
   *
   * for all TVD metrics and summary metrics, including:
   *
   * - water
   * - energy
   * - productivity
   * - cost avoidance
   * - replacement
   * - repair
   * - tvd_overall
   *
   * The UI should treat API-provided monetary values as immutable and should
   * not recalculate standard TVD metrics or summary totals.
   *
   * The only exception is Custom Tiles, whose values are generated entirely
   * in the client and therefore require client-side aggregation into the
   * displayed TVD total.
   *
   * Desired architecture:
   *
   * API
   *   → Calculates standard metrics
   *   → Calculates standard TVD totals
   *
   * UI
   *   → Displays API results
   *   → Calculates custom tile contributions only
   *   → Merges custom tile totals into displayed results
   *
   * This separation avoids duplicated calculation logic across the frontend
   * and backend and ensures a single source of truth for TVD calculations.
   */
  /**
   * TODO (Orchestration Refactor):
   * This method currently performs multiple responsibilities:
   * - Fetching TVD data from the API
   * - Updating internal TVD state
   * - Rebuilding presentation selections
   * - Updating metric visibility configuration
   * - Emitting updated models to subscribers
   *
   * As the codebase adopts the Orchestrator pattern, consider moving the
   * workflow coordination into a dedicated orchestrator and reducing this
   * method to a data-service responsibility focused on retrieving and
   * persisting TVD data.
   *
   * Desired end state:
   * - Orchestrator coordinates workflow
   * - TVDService performs data retrieval and state updates
   * - Builders construct payloads
   * - Resolvers determine configuration
   */
  async refreshTVDDataWithAssumptions(
    assumptions: MetricCostAssumptions
  ): Promise<void> {
    try {
      this.setShowLoadingOverlay(true);

      const groupID = this.getGroupId();

      // Call existing API wrapper (with assumptions)
      const response = await this.getTVDData(assumptions);
      if (!response) {
        return;
      }

      // Replace base TVD response (API is source of truth)
      this.tvdResponse = response;

      // Rebuild selections model (same pattern as default load)
      const selectedGroup = this.tvdResponse.find(group => group.id === groupID);

      this.overallTVDModelSelections = {
        id: groupID,
        brandGroup: selectedGroup?.brandGroup || '',
        indvMetrics: JSON.parse(JSON.stringify(selectedGroup?.indvMetrics || [])),
        summMetrics: JSON.parse(JSON.stringify(selectedGroup?.summMetrics || []))
      };

      // Update config visibility (existing behavior)
      const metricResponse = this.checkMetricPresence(this.tvdResponse, this.division);
      this.updateConfigBasedOnPresence(metricResponse, this.division);

      // IMPORTANT: Do NOT clear custom tiles (UI still owns them)

      // TODO (Orchestration Boundary):
      // confirmDataUpdate() represents a state transition rather than
      // a data retrieval concern. Consider whether model refresh and
      // subscriber notification should ultimately be coordinated by
      // an orchestrator instead of the data service.

      // Emit new model → triggers UI refresh
      this.confirmDataUpdate();

    } catch (error) {
      console.error('Error refreshing TVD data with assumptions', error);

      this._alertService.addAlert({
        message: 'Failed to recalculate TVD data. Please try again.',
        id: Math.random()
      });
    } finally {
      this.setShowLoadingOverlay(false);
    }
  }

  /**
   * Builds a preview-only TVD model for the supplied cost assumptions.
   *
   * This is the read-only counterpart of {@link refreshTVDDataWithAssumptions}:
   * it uses the same API (the single source of truth for rate-based
   * calculations) but intentionally performs NO state transition.
   *
   * Specifically it does NOT:
   * - replace `tvdResponse`
   * - rebuild `overallTVDModelSelections`
   * - update metric visibility configuration
   * - emit on `tvdSource` (so the TVD main page is left untouched)
   *
   * The returned model is built from a deep clone of the API response so that
   * preview consumers can never mutate applied TVD state. Callers must treat
   * the result as throw-away data that is discarded when the preview is closed.
   *
   * @param assumptions Cost assumptions entered by the user, not yet saved.
   * @returns A detached TVD model, or undefined when the API returns no data.
   */
  async buildPreviewTvdModel(
    assumptions: MetricCostAssumptions
  ): Promise<TVDModel | undefined> {
    try {
      this.setShowLoadingOverlay(true);

      const groupID = this.getGroupId();

      const response = await this.getTVDData(assumptions);
      if (!response) {
        return undefined;
      }

      // Detach the preview data from applied state and from the API response object.
      const previewResponse: TVDMetricGroupResponse[] = JSON.parse(JSON.stringify(response));
      const selectedGroup = previewResponse.find(group => group.id === groupID);

      const previewSelections: TVDMetricGroupResponse = {
        id: groupID,
        brandGroup: selectedGroup?.brandGroup || '',
        indvMetrics: JSON.parse(JSON.stringify(selectedGroup?.indvMetrics || [])),
        summMetrics: JSON.parse(JSON.stringify(selectedGroup?.summMetrics || []))
      };

      return new TVDModel(
        previewResponse,
        [previewSelections].concat(previewResponse.filter(group => group.id !== groupID)),
        [],
        this.custTileDict ? new Map(this.custTileDict) : this.custTileDict,
        this.addedMetrics ? [...this.addedMetrics] : this.addedMetrics
      );

    } catch (error) {
      console.error('Error building TVD preview data with assumptions', error);

      this._alertService.addAlert({
        message: 'Failed to preview TVD data. Please try again.',
        id: Math.random()
      });

      return undefined;
    } finally {
      this.setShowLoadingOverlay(false);
    }
  }

  /**
   * <summary>
   * Recalculates the Total Value Delivered metric by incorporating custom tile values
   * into the API-provided totals.
   * </summary>
   *
   * <param name="tvdModel">
   * The current TVD model received from the API. This model already includes all
   * cost assumption adjustments and base calculations.
   * </param>
   *
   * <param name="customTiles">
   * A dictionary of user-defined custom tiles that contain additional monetary values
   * not provided by the API.
   * </param>
   *
   * <returns>
   * void. Updates the TVD model in-place and emits a new reference to trigger UI updates.
   * </returns>
   *
   * <remarks>
   * - This method ONLY adjusts the TOTALVALUEDELIVERED metric.
   * - It does NOT recalculate individual API metrics.
   * - It preserves original and API-adjusted values.
   * - Custom tile values are added on top of the modified API total.
   *
   * ARCHITECTURAL CONSTRAINT:
   * - This method MUST NOT apply or modify any cost assumption rates.
   * - All rate-based calculations (including cost assumptions adjustments, conversions,
   *   and business-rule-driven recalculations) MUST be performed exclusively in the API.
   * - The tvdModel and tvdResponse are assumed to already contain fully adjusted values
   *   returned from the API.
   *
   * INTENDED SCOPE:
   * - Allowed:
   *   • Aggregation of custom tile values
   *   • Updating derived totals for presentation
   *
   * - NOT Allowed:
   *   • Applying cost assumption rates
   *   • Recomputing base metric values
   *   • Modifying API-provided monetary contributions
   *
   * This boundary ensures a single source of truth for all rate-based calculations
   * and prevents duplication of business logic between the UI and API.
   * </remarks>
   *
   * <exception>
   * Logs a warning if the TOTALVALUEDELIVERED metric cannot be found.
   * </exception>
   */
  /**
   * TODO (Workflow Ownership):
   * This method is currently invoked by callers after TVD refresh operations.
   *
   * Evaluate whether custom tile aggregation should become part of the
   * TVD refresh workflow itself so callers do not need to remember to:
   *
   *   refreshTVDDataWithAssumptions(...)
   *   → recalculateTvdTotals(...)
   *
   * Desired end state:
   * - Refresh workflow remains atomic
   * - Consumers perform a single operation
   * - Total recalculation occurs automatically when appropriate
   */
  recalculateTvdTotals(
    tvdModel: TVDModel | undefined,
    customTiles: Map<string, custTile> | undefined
  ): void {

    if (!tvdModel) {
      return;
    }

    try {
      let totalCustomValue = 0;

      customTiles?.forEach(tile => {
        const numericValue = Number(
          (tile.cost ?? '0').toString().replace(/,/g, '')
        ) || 0;
        totalCustomValue += numericValue;
      });

      let groupID: tvdGroupIds = this.getGroupId();
      const currentProgram = this.getCurrentProgram();
      if (TvdProgramToGroupIdMapping.has(currentProgram)) {
        groupID = TvdProgramToGroupIdMapping.get(currentProgram)!;
      }

      let group = this.tvdResponse.find(g => g.id === groupID);

      if (!group) {
        // HACK:
        // Some code paths still attempt to resolve the DEFAULT TVD group
        // even when the response only contains the PEST group for Pest
        // division. When DEFAULT is not found, fall back to PEST so that
        // TOTALVALUEDELIVERED can still be emitted.
        //
        // TODO:
        // Investigate why group resolution is inconsistent. The long-term
        // solution should be to standardize group lookup so all consumers
        // resolve the same group ID instead of falling back here.
        //
        // We should also evaluate whether pest_tvd is providing any
        // meaningful distinction from default_tvd or whether the backend
        // can return a single canonical group identifier.
        if (groupID === tvdGroupIds.DEFAULT) {
          // Fallback to pest_tvd  if default_tvd is not found.
          group = this.tvdResponse.find(g => g.id === tvdGroupIds.PEST);

        }
      }

      if (!group) {
          return;
        }

      const totalMetric = group.summMetrics.find(
        metric => metric.id === eroiIds.TOTALVALUEDELIVERED
      );

      if (!totalMetric) {
        console.warn('TOTALVALUEDELIVERED metric not found');
        return;
      }

      //
      // API is the source of truth.
      // Use modified value when present,
      // otherwise fall back to original value.
      //
      const apiTotalString = totalMetric.monetaryContribution ?? '0';

      //
      // No custom tiles?
      // Leave API total completely untouched.
      //
      if (totalCustomValue === 0) {

        this.totalTotalValueDelivered$.next({
          ...totalMetric
        });
        return;
      }

      //
      // Custom tiles exist:
      // add them to API total.
      //
      const apiTotal = Number(apiTotalString?.replace(/,/g, '') || 0);
      const modifiedApiTotal = Number(totalMetric.modifiedMonetaryContribution?.replace(/,/g, '') ) || 0;
      const isModified = modifiedApiTotal > 0;

      const newTotal = isModified ?
        modifiedApiTotal + totalCustomValue :
        apiTotal + totalCustomValue;


      // ALSO update selections model (for PPT)
      const selectionMetric = this.overallTVDModelSelections.summMetrics.find(
        metric => metric.id === eroiIds.TOTALVALUEDELIVERED
      );

      if (selectionMetric) {
        selectionMetric.monetaryContribution = apiTotal.toLocaleString();
        selectionMetric.modifiedMonetaryContribution = newTotal.toLocaleString();
      }

      this.totalTotalValueDelivered$.next({
        ...totalMetric,
        modifiedMonetaryContribution: newTotal.toLocaleString()
      });

    } catch (error) {
      console.error('Error recalculating totals with custom tiles', error);
    }
  }
}
